import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Outreach.css';

const OutreachEditor = ({
    node,
    onBack,
    onSave,
    onDelete,
    setHasUnsavedChanges,
    campaign,
    isInline,
    connectedSenders = [],
    onNodeUpdate,
    onConnectSender,
    onDisconnectSender,
    isThreadReply = false,
    threadSubject = ''
}) => {
    const { t } = useTranslation('sourcing');
    const editorRef = useRef(null); // Container for Quill
    const quillInstance = useRef(null); // Store the instance directly
    const nodeRef = useRef(node); // Track current node for event handlers

    // Keep nodeRef in sync with the current node prop
    useEffect(() => {
        nodeRef.current = node;
    }, [node]);

    // Initialize state from node data
    const [subject, setSubject] = useState(node?.subject || '');
    const [from, setFrom] = useState(node?.from || '');
    const [body, setBody] = useState(node?.body || '');
    const [cc, setCc] = useState(node?.cc || '');
    const [bcc, setBcc] = useState(node?.bcc || '');
    const [isDirty, setIsDirty] = useState(false);

    // UI state
    const [showCcBcc, setShowCcBcc] = useState(!!(node?.cc || node?.bcc));
    const [showAiPromptPopup, setShowAiPromptPopup] = useState(false);
    const [showConnectPopup, setShowConnectPopup] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [quillReady, setQuillReady] = useState(false);

    // ========================================================================
    // QUILL INITIALIZATION (Replicating Home.js Pattern)
    // ========================================================================
    useEffect(() => {
        const loadExternalDependencies = () => {
            // Check if already loaded
            if (document.querySelector('script[src="https://cdn.quilljs.com/1.3.6/quill.js"]')) {
                return;
            }

            // Quill CSS
            const quillCSS = document.createElement('link');
            quillCSS.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
            quillCSS.rel = 'stylesheet';
            document.head.appendChild(quillCSS);

            // Quill JS
            const quillJS = document.createElement('script');
            quillJS.src = 'https://cdn.quilljs.com/1.3.6/quill.js';
            document.head.appendChild(quillJS);
        };

        loadExternalDependencies();

        const initQuill = () => {
            if (window.Quill && editorRef.current && !quillInstance.current) {
                try {
                    // 1. Register Custom Blot
                    const Embed = window.Quill.import('blots/embed');
                    class VariableBlot extends Embed {
                        static create(value) {
                            let node = super.create();
                            node.setAttribute('data-variable', value);
                            node.innerHTML = value;
                            node.contentEditable = false;
                            return node;
                        }
                        static value(node) {
                            return node.getAttribute('data-variable');
                        }
                    }
                    VariableBlot.blotName = 'variable';
                    VariableBlot.tagName = 'span';
                    VariableBlot.className = 'outreach-variable-chip';
                    window.Quill.register(VariableBlot, true);

                    // 2. Initialize Editor
                    const quill = new window.Quill(editorRef.current, {
                        theme: 'snow',
                        placeholder: t('outreach.editor.bodyPlaceholder'),
                        modules: {
                            toolbar: [
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                ['link', 'clean']
                            ],
                            clipboard: { matchVisual: false }
                        }
                    });

                    quillInstance.current = quill;
                    setQuillReady(true);

                    // 3. Set Initial Content
                    if (nodeRef.current?.body) {
                        quill.clipboard.dangerouslyPasteHTML(nodeRef.current.body);
                    }

                    // 4. Bind Change Event - use nodeRef to get current node
                    quill.on('text-change', () => {
                        const html = editorRef.current.querySelector('.ql-editor').innerHTML;
                        setBody(html);

                        setIsDirty(true);
                        if (setHasUnsavedChanges) setHasUnsavedChanges(true);

                        // Use nodeRef.current to always get the latest node
                        if (onNodeUpdate && nodeRef.current) {
                            onNodeUpdate({
                                ...nodeRef.current,
                                body: html
                            });
                        }
                    });

                } catch (error) {
                    console.error('Error initializing Quill:', error);
                }
            }
        };

        const checkQuill = setInterval(() => {
            if (window.Quill) {
                clearInterval(checkQuill);
                setTimeout(initQuill, 100);
            }
        }, 100);

        return () => clearInterval(checkQuill);
    }, []); // Run once on mount

    // Sync body updates from props if node changes (e.g. switching nodes)
    // IMPORTANT: This handles "Loading new node"
    useEffect(() => {
        if (node && quillInstance.current && editorRef.current) {
            // Always reset Quill content when node ID changes
            const newContent = node.body || '';
            quillInstance.current.clipboard.dangerouslyPasteHTML(newContent);
            setBody(newContent);
        }
    }, [node?.id]); // Only re-set if ID changes


    // Load data from node when it changes (Standard State - non-body fields)
    useEffect(() => {
        if (node) {
            // Apply subject lock for thread replies
            if (isThreadReply && threadSubject) {
                setSubject(threadSubject);
                // IMPORTANT: Sync the inherited subject to the node data if it doesn't match
                if (node.subject !== threadSubject && onNodeUpdate) {
                    onNodeUpdate({
                        ...node,
                        subject: threadSubject
                    });
                }
            } else {
                setSubject(node.subject || '');
            }

            setFrom(node.from || '');
            setCc(node.cc || '');
            setBcc(node.bcc || '');
            setShowCcBcc(!!(node.cc || node.bcc));

            setIsDirty(false);
        }
    }, [node?.id, isThreadReply, threadSubject]);

    // Sync from field when it changes externally (e.g., sender propagation from step 1)
    useEffect(() => {
        if (node?.from && node.from !== from) {
            setFrom(node.from);
        }
    }, [node?.from]);

    // Track changes
    useEffect(() => {
        if (isDirty && setHasUnsavedChanges) {
            setHasUnsavedChanges(true);
        }
    }, [isDirty, setHasUnsavedChanges]);

    const handleChange = (setter, value, field) => {
        // Special handler for sender dropdown actions
        if (field === 'from') {
            if (value === 'CONNECT_NEW') {
                setShowConnectPopup(true);
                return;
            }
        }

        setter(value);
        setIsDirty(true);
        if (setHasUnsavedChanges) setHasUnsavedChanges(true);

        // Sync with parent immediately so switching nodes is seamless
        if (onNodeUpdate) {
            onNodeUpdate({
                ...node,
                subject: field === 'subject' ? value : subject,
                from: field === 'from' ? value : from,
                cc: field === 'cc' ? value : cc,
                bcc: field === 'bcc' ? value : bcc,
                body: field === 'body' ? value : body
            });
        }
    };

    const insertVariable = (variableText) => {
        if (!quillInstance.current) return;
        const editor = quillInstance.current;
        const range = editor.getSelection(true);

        let index = range ? range.index : editor.getLength();

        // Insert the custom blot
        editor.insertEmbed(index, 'variable', variableText);
        // Move cursor after the chip and add a space
        editor.setSelection(index + 1);
        editor.insertText(index + 1, ' ');
    };

    const handleVariableClick = (variable) => {
        if (variable === 'profileSpecific') {
            setShowAiPromptPopup(true);
        } else {
            const varTag = `{{${t(`outreach.variables.${variable}`)}}}`;
            insertVariable(varTag);
        }
    };

    const handleSaveAiPrompt = () => {
        if (aiPrompt.trim()) {
            const varTag = `{{${aiPrompt}}}`;
            insertVariable(varTag);
        }
        setShowAiPromptPopup(false);
        setAiPrompt('');
    };

    const coreVariables = ['profileSpecific', 'firstName', 'lastName', 'company', 'jobTitle'];

    // Sender Helper
    const currentSender = connectedSenders.find(s => s.email === from);

    return (
        <div className="outreach-editor" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Inline Header with Delete, Save, and Close */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>{t('outreach.editor.editingTitle', { title: node?.title || 'Email' })}</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={onDelete}
                        style={{
                            background: 'transparent', border: '1px solid #e74c3c', color: 'var(--app-tone-e74c3c, #e74c3c)',
                            padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                      data-light-control={"quiet-danger"}>
                        <i className="fa-regular fa-trash-can"></i> {t('outreach.editor.delete')}
                    </button>
                    <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', cursor: 'pointer', marginLeft: '4px' }}  data-light-control={"quiet"}>
                        <i className="fa-solid fa-xmark" style={{ fontSize: '20px' }}></i>
                    </button>
                </div>
            </div>

            <div className="editor-card" style={{ background: 'var(--app-surface, #0a1930)', borderRadius: '12px', padding: '24px', border: "1px solid var(--app-border, rgba(86,141,237,0.2))" }}>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                    {/* Subject Row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ fontWeight: "var(--app-weight-strong, 700)", whiteSpace: 'nowrap', minWidth: '70px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', fontSize: '13px' }}>{t('outreach.editor.subject')}</label>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => handleChange(setSubject, e.target.value, 'subject')}
                                disabled={isThreadReply}
                                style={{
                                    width: '100%', padding: '12px 16px', borderRadius: '8px', border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))",
                                    outline: 'none', fontWeight: 600, color: isThreadReply ? '#666' : '#000',
                                    fontFamily: 'Montserrat', background: isThreadReply ? '#e0e0e0' : '#fff',
                                    cursor: isThreadReply ? 'not-allowed' : 'text'
                                }}
                                placeholder={t('outreach.editor.subjectPlaceholder', 'Enter email subject...')}
                            />
                            {isThreadReply && (
                                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                                    <i className="fa-solid fa-lock" style={{ marginRight: '4px' }}></i>
                                    {t('outreach.editor.replyThread', 'Reply to thread')}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* From Row - with connected senders dropdown */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <label style={{ fontWeight: "var(--app-weight-strong, 700)", whiteSpace: 'nowrap', minWidth: '70px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', fontSize: '13px' }}>{t('outreach.editor.from')}</label>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, maxWidth: '450px' }}>
                                <select
                                    value={from}
                                    onChange={(e) => handleChange(setFrom, e.target.value, 'from')}
                                    disabled={isThreadReply}
                                    style={{
                                        flex: 1, padding: '12px 16px', borderRadius: '8px',
                                        border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))", outline: 'none', fontFamily: 'Montserrat',
                                        background: isThreadReply ? '#e0e0e0' : '#fff',
                                        color: isThreadReply ? '#666' : '#000',
                                        cursor: isThreadReply ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <option value="">{t('outreach.editor.selectSender', 'Select sender email...')}</option>
                                    {connectedSenders.map(sender => (
                                        <option key={sender.id} value={sender.email}>
                                            {sender.email} ({sender.provider})
                                        </option>
                                    ))}
                                    <option disabled>────────────────</option>
                                    <option value="CONNECT_NEW" style={{ fontWeight: "var(--app-weight-strong, bold)", color: '#568ded' }}>
                                        + {t('outreach.editor.connectNew', 'Connect new account...')}
                                    </option>
                                </select>
                                {isThreadReply && (
                                    <div title={t('outreach.editor.senderLocked', 'Same as Step 1')} style={{ color: '#888', marginLeft: '4px' }}>
                                        <i className="fa-solid fa-lock"></i>
                                    </div>
                                )}
                            </div>

                        </div>
                        <button
                            onClick={() => setShowCcBcc(!showCcBcc)}
                            style={{ background: 'transparent', border: 'none', color: '#568ded', textDecoration: 'none', fontSize: '13px', fontWeight: "var(--app-weight-strong, 700)", cursor: 'pointer', padding: '0 10px' }}
                          data-light-control={"quiet"}>
                            {showCcBcc ? t('outreach.editor.showLess') : t('outreach.editor.addCcBcc')}
                        </button>
                    </div>

                    {/* CC & BCC Row */}
                    {showCcBcc && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', background: 'var(--app-surface, rgba(255,255,255,0.03))', borderRadius: '12px', border: "1px dashed var(--app-border, rgba(86,141,237,0.2))" }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <label style={{ fontWeight: "var(--app-weight-strong, 700)", whiteSpace: 'nowrap', minWidth: '70px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)', fontSize: '12px' }}>{t('outreach.editor.cc')}</label>
                                <input
                                    type="text"
                                    value={cc}
                                    onChange={(e) => handleChange(setCc, e.target.value, 'cc')}
                                    style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: 'none', outline: 'none', fontFamily: 'Montserrat', fontSize: '13px' }}
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <label style={{ fontWeight: "var(--app-weight-strong, 700)", whiteSpace: 'nowrap', minWidth: '70px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)', fontSize: '12px' }}>{t('outreach.editor.bcc')}</label>
                                <input
                                    type="text"
                                    value={bcc}
                                    onChange={(e) => handleChange(setBcc, e.target.value, 'bcc')}
                                    style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: 'none', outline: 'none', fontFamily: 'Montserrat', fontSize: '13px' }}
                                    placeholder="email@example.com"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '10px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)', textTransform: 'none', letterSpacing: '0.5px' }}>{t('outreach.editor.candidateVariables')}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {coreVariables.map(vKey => (
                            <button
                                key={vKey}
                                onClick={() => handleVariableClick(vKey)}
                                style={{
                                    background: 'var(--app-surface, rgba(86,141,237,0.15))', color: '#568ded', border: "1px solid var(--app-border-strong, rgba(86,141,237,0.3))",
                                    padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: "var(--app-weight-strong, 700)", cursor: 'pointer',
                                    fontFamily: 'Montserrat', transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.background = 'var(--app-surface, rgba(86,141,237,0.25))'}
                                onMouseOut={(e) => e.target.style.background = 'var(--app-surface, rgba(86,141,237,0.15))'}
                              data-light-control={"quiet"}>
                                {t(`outreach.variables.${vKey}`)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="outreach-quill-wrapper" style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                    <div ref={editorRef} style={{ height: '350px', color: "var(--app-neutral-ink, #000)", fontFamily: 'Montserrat' }}></div>
                </div>

            </div>

            {/* AI Prompt Popup */}
            {showAiPromptPopup && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div className="modal-content" style={{
                        background: '#fff', width: '500px', padding: '30px', borderRadius: '12px', color: "var(--app-neutral-ink, #000)",
                        textAlign: 'center'
                    }}>
                        <h3 style={{ fontFamily: 'Montserrat', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '16px' }}>
                            {t('outreach.editor.aiPromptTitle')}
                        </h3>
                        <p style={{ fontFamily: 'Montserrat', fontSize: '14px', marginBottom: '20px', color: '#555' }}>
                            {t('outreach.editor.aiPromptDesc')}
                        </p>

                        <input
                            type="text"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder={t('outreach.editor.aiPromptPlaceholder')}
                            style={{
                                width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd',
                                marginBottom: '20px', fontFamily: 'Montserrat'
                            }}
                        />

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            <button onClick={() => setShowAiPromptPopup(false)} style={{ padding: '10px 20px', borderRadius: '6px', background: '#eee', border: 'none', fontFamily: 'Montserrat', cursor: 'pointer', color: "var(--app-neutral-ink, #333)" }}  data-light-control={"quiet"}>{t('outreach.buttons.cancel')}</button>
                            <button onClick={handleSaveAiPrompt} style={{ '--light-control-fill': '#568ded', padding: '10px 20px', borderRadius: '6px', background: '#568ded', color: '#fff', border: 'none', fontFamily: 'Montserrat', fontWeight: 600, cursor: 'pointer' }}  data-light-control={"filled"}>{t('outreach.editor.saveInstruction')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Connect Account Popup */}
            {showConnectPopup && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div className="modal-content" style={{
                        background: '#fff', width: '450px', padding: '30px', borderRadius: '12px', color: "var(--app-neutral-ink, #000)",
                        textAlign: 'center', position: 'relative'
                    }}>
                        <button
                            onClick={() => setShowConnectPopup(false)}
                            style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888' }}
                          data-light-control={"quiet"}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>

                        <div style={{ width: '60px', height: '60px', background: 'var(--app-surface, rgba(86,141,237,0.1))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <i className="fa-solid fa-link" style={{ fontSize: '24px', color: '#568ded' }}></i>
                        </div>

                        <h3 style={{ fontFamily: 'Montserrat', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '10px', color: "var(--app-neutral-ink, #333)" }}>
                            {t('outreach.connect.title', 'Connect Email Account')}
                        </h3>
                        <p style={{ fontFamily: 'Montserrat', fontSize: '14px', marginBottom: '24px', color: '#666', lineHeight: '1.5' }}>
                            {t('outreach.connect.subtitle', 'Connect your email inbox to send automated campaigns and track replies directly within JOBSEEQR.')}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    if (onConnectSender) onConnectSender('google');
                                    setShowConnectPopup(false);
                                }}
                                style={{
                                    padding: '12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600,
                                    transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    color: "var(--app-neutral-ink, #333)" // Explicit text color
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.borderColor = '#4285F4';
                                    e.currentTarget.style.background = '#f8faff';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.borderColor = '#ddd';
                                    e.currentTarget.style.background = '#fff';
                                }}
                              data-light-control={"quiet"}>
                                <img
                                    src="data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Cpath fill='%23EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/%3E%3Cpath fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/%3E%3Cpath fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/%3E%3Cpath fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/%3E%3C/svg%3E"
                                    alt="Google"
                                    style={{ width: '20px', height: '20px' }}
                                />
                                {t('outreach.connect.google', 'Connect with Gmail')}
                            </button>

                            <button
                                disabled={true}
                                style={{
                                    padding: '12px', borderRadius: '8px', border: '1px solid #eee', background: '#f5f5f5',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    cursor: 'not-allowed', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600,
                                    color: '#999'
                                }}
                              data-light-control={"quiet"}>
                                <i className="fa-brands fa-microsoft" style={{ fontSize: '20px' }}></i>
                                {t('outreach.connect.outlook', 'Connect with Outlook (Coming Soon)')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OutreachEditor;

